import { useCallback, useRef, useState } from 'react';

import { useGeolocation } from 'state/geolocation';
import { useServiceModeContext } from 'state/service-mode';
import { useUIContext } from 'state/ui';

import { useFindAvailableDeliveryRestaurant } from './use-find-available-delivery-restaurant';
import { useFindAvailablePickupRestaurant } from './use-find-available-pickup-restaurant';

export const useRestaurantSearch = () => {
  const searchExecutedRef = useRef(false);
  const [isDeliverySearchComplete, setDeliverySearchCompleted] = useState(false);
  const { isDelivery } = useServiceModeContext();
  const { setIsStoreOrderUnavailableDialogOpen } = useUIContext();
  const { isPermissionGranted } = useGeolocation();
  const {
    findFirstOrderableRestaurantForDelivery,
    availableRestaurant: availableDeliveryRestaurant,
    clearSearchState: clearDeliverySearchState,
    isLoading: isSearchingDeliveryRestaurant,
  } = useFindAvailableDeliveryRestaurant({ setDeliverySearchCompleted });

  const {
    findFirstOrderableRestaurantForPickUp,
    isLoading: isSearchingPickupRestaurant,
    availableRestaurant,
    clearSearchState: clearPickupSearchState,
  } = useFindAvailablePickupRestaurant();

  const clearAllSearchState = useCallback(() => {
    clearPickupSearchState();
    clearDeliverySearchState();
    setDeliverySearchCompleted(false);
    setIsStoreOrderUnavailableDialogOpen(false);
    searchExecutedRef.current = false;
  }, [clearPickupSearchState, clearDeliverySearchState, setIsStoreOrderUnavailableDialogOpen]);

  const executeSearch = useCallback(async () => {
    if (searchExecutedRef.current) {
      return;
    }

    if (isDelivery) {
      await findFirstOrderableRestaurantForDelivery();
      searchExecutedRef.current = true;
    } else {
      if (isPermissionGranted) {
        await findFirstOrderableRestaurantForPickUp();
        searchExecutedRef.current = true;
      }
    }
  }, [
    isDelivery,
    findFirstOrderableRestaurantForDelivery,
    findFirstOrderableRestaurantForPickUp,
    isPermissionGranted,
  ]);

  return {
    findFirstOrderableRestaurantForPickUp,
    findFirstOrderableRestaurantForDelivery,
    isRestaurantSearchLoading: isSearchingDeliveryRestaurant || isSearchingPickupRestaurant,
    availableRestaurantForDelivery: availableDeliveryRestaurant,
    availableRestaurantForPickUp: availableRestaurant,
    isDeliverySearchComplete,
    clearAllSearchState,
    executeSearch,
    isDelivery,
  };
};
